import {
  Input,
  InputNumber,
  Modal,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { AppSetting } from 'utils/appSettings';
import { useRbac } from 'features/rbac/rbac';
import { ERbacPermissions } from 'features/rbac/rbacPermissionsList';
import {
  CommonTableHeader,
  IEditInventoryListingDatum,
  IPriceComparisonListing,
} from 'models/interfaces';
import { ICustomError } from 'models/interfaces/ICustomError';
import React, { useState } from 'react';
import { ZonesService } from 'services/SkyboxServices';
import { LoggerService } from 'sharedServices';
import CustomError from 'controls/CustomError/CustomError';
import CommonReportTable from 'utils/table/CommonReportTable';
import InlineEdit from 'utils/table/InlineEdit';
import LoadingSpinner from 'utils/sharedComponents/LoadingSpinner';
import {
  formatAccountingValues,
  formatNumericValues,
} from 'utils/commonFunction';
import { formatDateGivenFormatString } from 'utils/formatTimePeriod';
import { daysToEventColorCheck } from 'utils/commonFunction';
import {
  getTagClassForZones,
  getTagClassForRecommendation,
  getUserName,
} from 'utils/commonFunction';
import autoAdjustmentEnabled from '../../../../assets/images/enabled-roundel.svg';
import autoAdjustmentDisabled from '../../../../assets/images/disabled-roundel.svg';
import { showBrokerOrSeatMapCreatorCheckerLister } from 'views/SkyBox/Trading/Events/eventsUtils';

const expectedMarketPriceFactor = Number(
  process.env.REACT_APP_EXPECTED_MARKET_PRICE_FACTOR,
);

// Short labels matching tbl_zone_availability_master exactly (0=None, 1=High,
// 2=Medium, 3=Low, 4=TBD) -- the same short text Zone Listings' own tag shows,
// as opposed to the longer "To Be Decided" fallback text this screen's own
// availability_display_alias field carries.
const zoneAvailabilityShortLabels: Record<number, string> = {
  0: 'None',
  1: 'High',
  2: 'Medium',
  3: 'Low',
  4: 'TBD',
};

const PriceComparisonTable: React.FC<{
  showLoader: boolean;
  dataFetchError: ICustomError | null;
  listings: IPriceComparisonListing[] | null;
  queryParams: any;
  setQueryParams: Function;
  onRowUpdated: (
    purchaseLineId: number,
    changes: Partial<IPriceComparisonListing>,
  ) => void;
}> = ({
  showLoader,
  dataFetchError,
  listings,
  queryParams,
  setQueryParams,
  onRowUpdated,
}) => {
  const { hasPermissions } = useRbac();

  const canEditListing = hasPermissions([
    ERbacPermissions.ZONES_SAVE_EDITED_LISTINGS,
  ]);
  const canToggleBroadcast = hasPermissions([
    ERbacPermissions.ZONES_ACTIVE_LISTING_BROADCAST_ACTION,
  ]);

  // Section/Row/List Price are drafted locally (per purchase_line_id) instead
  // of saving immediately on blur -- the per-row save icon only appears once
  // a field's draft differs from the real singleData value (same comparison
  // Zone Listings' own isDataChanged()/rowSaveClickHandler pattern uses), and
  // disappears again the moment the user reverts it back to the original
  // value, with no bulk/batch "Save Changes" bar needed for this screen.
  const [editedRows, setEditedRows] = useState<
    Record<
      number,
      { section?: string; row?: string; list_price?: number }
    >
  >({});

  const getEditedField = <K extends 'section' | 'row' | 'list_price'>(
    purchaseLineId: number,
    field: K,
    original: IPriceComparisonListing[K],
  ): IPriceComparisonListing[K] => {
    const edited = editedRows[purchaseLineId]?.[field];
    return edited !== undefined ? (edited as IPriceComparisonListing[K]) : original;
  };

  const setEditedField = (
    purchaseLineId: number,
    field: 'section' | 'row' | 'list_price',
    value: string | number,
  ) => {
    setEditedRows((prev) => ({
      ...prev,
      [purchaseLineId]: { ...prev[purchaseLineId], [field]: value },
    }));
  };

  const isRowDirty = (singleData: IPriceComparisonListing) => {
    const edited = editedRows[singleData.purchase_line_id];
    if (!edited) return false;
    return (
      (edited.section !== undefined && edited.section !== singleData.section) ||
      (edited.row !== undefined && edited.row !== singleData.row) ||
      (edited.list_price !== undefined &&
        edited.list_price !== singleData.list_price)
    );
  };

  // Tracks rows mid-save so the floppy icon swaps for a spinner while the
  // update-section-row-price / force-sync-skybox / remap sequence is in
  // flight, same as Zone Listings' own singleData.is_updating gate.
  const [savingRows, setSavingRows] = useState<Record<number, boolean>>({});

  const saveRow = async (singleData: IPriceComparisonListing) => {
    const edited = editedRows[singleData.purchase_line_id];
    if (!edited) return;
    const changes: Partial<
      Pick<IEditInventoryListingDatum, 'newSection' | 'newRow' | 'new_list_price'>
    > = {};
    if (edited.section !== undefined && edited.section !== singleData.section) {
      changes.newSection = edited.section;
    }
    if (edited.row !== undefined && edited.row !== singleData.row) {
      changes.newRow = edited.row;
    }
    if (
      edited.list_price !== undefined &&
      edited.list_price !== singleData.list_price
    ) {
      changes.new_list_price = edited.list_price;
    }
    if (Object.keys(changes).length === 0) return;
    setSavingRows((prev) => ({ ...prev, [singleData.purchase_line_id]: true }));
    const didSave = await saveSectionRowPrice(singleData, changes);
    setSavingRows((prev) => {
      const next = { ...prev };
      delete next[singleData.purchase_line_id];
      return next;
    });
    // Only clear the draft (and hide the save icon) once the save actually
    // succeeds -- saveSectionRowPrice swallows its own errors (shows a toast
    // instead of throwing), so without checking its return value a failed
    // save would still clear the dirty flag and the icon would vanish even
    // though nothing was persisted, leaving the user unable to retry.
    if (didSave) {
      setEditedRows((prev) => {
        const next = { ...prev };
        delete next[singleData.purchase_line_id];
        return next;
      });
    }
  };

  const saveSectionRowPrice = async (
    singleData: IPriceComparisonListing,
    changes: Partial<
      Pick<
        IEditInventoryListingDatum,
        'newSection' | 'newRow' | 'new_list_price'
      >
    >,
  ) => {
    const payload: IEditInventoryListingDatum = {
      inventory_id: singleData.inventory_id,
      inventory_ids: singleData.inventory_ids,
      is_split: singleData.is_split,
      oldSection: singleData.section,
      newSection: singleData.section,
      oldRow: singleData.row,
      newRow: singleData.row,
      old_list_price: singleData.list_price,
      new_list_price: singleData.list_price,
      update_price: false,
      update_section_row: false,
      auto_adjustment: singleData.auto_adjustment,
      ...changes,
    };
    if ('newSection' in changes || 'newRow' in changes) {
      payload.update_section_row = true;
    }
    if ('new_list_price' in changes) {
      payload.update_price = true;
    }
    // Mirrors Zone Listings' rowSaveClickHandler: an edited listing isn't
    // "saved" until it has also been pushed back out to SkyBox and remapped
    // against its zone -- saving section/row/price alone (without the two
    // calls below) leaves SkyBox out of sync with what this screen shows.
    let messageKey = 'priceComparisonUpdateListing';
    message.loading({
      content: 'Updating listing...',
      duration: 0,
      key: messageKey,
      className: 'toastMsg loadingMsg',
    });
    try {
      const updateResponse = await new ZonesService().updateListingSectionRowPrice([
        payload,
      ]);
      onRowUpdated(singleData.purchase_line_id, {
        section: payload.newSection,
        row: payload.newRow,
        list_price: payload.new_list_price,
      });
      let forceUpdateMessage = 'Syncing data from skybox to platform...';
      let messageDuration = 5;
      if (updateResponse.status === 200) {
        message.success({
          content: 'Listing Updated in Skybox.',
          duration: 5,
          key: messageKey,
          className: 'toastMsg savedSuccess',
        });
        messageKey = 'priceComparisonForceUpdate';
        message.loading({
          content: forceUpdateMessage,
          duration: 0,
          key: messageKey,
          className: 'toastMsg loadingMsg',
        });
        const forceUpdateResponse = await new ZonesService().forceUpdatePlatformSkyboxInventories(
          payload.inventory_ids,
        );
        if (forceUpdateResponse.data.data.is_mvw_refreshed) {
          forceUpdateMessage = 'Zone listing has been synced successfully!';
        } else {
          forceUpdateMessage =
            'The Listing has been updated successfully but it may take a while to appear on the platform. Data synchronization with SkyBox is in progress.';
          messageDuration = 10;
        }
      }
      message.success({
        content: forceUpdateMessage,
        duration: messageDuration,
        key: messageKey,
        className: 'toastMsg savedSuccess',
      });
      await new ZonesService().remapListingsWithZones(singleData.event_id);
      return true;
    } catch (error: any) {
      await new LoggerService().log({
        payload: error,
        function_name: 'saveSectionRowPrice',
      });
      message.error({
        content: 'Failed to update listing.',
        duration: 5,
        key: messageKey,
        className: 'toastMsg savedFailed',
      });
      return false;
    }
  };

  const toggleBroadcast = (singleData: IPriceComparisonListing) => {
    const nextStatus = !singleData.broadcast;
    Modal.confirm({
      title: `Are you sure you want to ${
        nextStatus ? 'broadcast' : 'un-broadcast'
      } this listing?`,
      okText: 'OK',
      cancelText: 'CANCEL',
      onOk: async () => {
        try {
          await new ZonesService().updateInventoryBroadcastStatus(
            singleData.inventory_id,
            nextStatus,
            singleData.purchase_line_id,
          );
          onRowUpdated(singleData.purchase_line_id, {
            broadcast: nextStatus,
          });
          message.success({
            content: 'Broadcast status has been updated successfully!',
            duration: 3,
            className: 'toastMsg savedSuccess',
          });
        } catch (error: any) {
          await new LoggerService().log({
            payload: error,
            function_name: 'toggleBroadcast',
          });
          message.error({
            content: 'Broadcast status has failed to update.',
            duration: 5,
            className: 'toastMsg savedFailed',
          });
        }
      },
    });
  };

  const commonWidth = 120;

  const headers: CommonTableHeader[] = [
    {
      title: 'Last Updated',
      dataIndex: 'updated_on',
      canSort: true,
      width: '2%',
      thStyle: { width: '2%' },
      dataRender: (_: string | null, otherData: any) => {
        const singleData: IPriceComparisonListing = otherData.data.singleData;
        if (!singleData.updated_by) {
          return <></>;
        }
        let lastUpdatedData: {
          compare_date: number | null;
          first_name: string | null;
          last_name: string | null;
        };
        try {
          lastUpdatedData = JSON.parse(singleData.updated_by);
        } catch {
          return <></>;
        }
        if (lastUpdatedData.compare_date === null) {
          return <></>;
        }

        let userName: React.ReactNode;
        if (singleData.is_from_desktop_app) {
          userName = singleData.is_desktop_final_success ? (
            <i className="da icon-desktop-app-success" />
          ) : (
            ''
          );
        } else if (singleData.is_web_final_success === true) {
          userName = <i className="da icon-fictionalCharacter-green" />;
        } else if (singleData.scraping_status_id === 2) {
          userName = getUserName(lastUpdatedData);
        }

        if (!userName) return <></>;
        if (lastUpdatedData.compare_date > 1) {
          return (
            <div>
              {lastUpdatedData.compare_date} days ago by {userName}
            </div>
          );
        } else if (lastUpdatedData.compare_date === 1) {
          return (
            <div>
              {lastUpdatedData.compare_date} day ago by {userName}
            </div>
          );
        } else if (lastUpdatedData.compare_date === 0) {
          return <div>Today by {userName}</div>;
        }
        return <></>;
      },
    },
    {
      title: 'EVENT NAME & VENUE',
      dataIndex: 'event_name',
      canSort: true,
      width: '5.5%',
      thStyle: { width: '5.5%' },
      dataRender: (eventName: string, otherData: any) => {
        const singleData: IPriceComparisonListing = otherData.data.singleData;
        return (
          <div className="text-capitalize" style={{ wordBreak: 'break-word' }}>
            <a
              target="_blank"
              className="eventNameVenue"
              rel="noopener noreferrer"
              href={AppSetting.EVENT_URL + singleData.event_id}
              style={{ whiteSpace: 'normal' }}
            >
              <strong>{eventName}</strong>
            </a>
            <br />
            {singleData.venue_name}: {singleData.venue_location}
            <br />
            {Boolean(singleData.venue_fees) && (
              <span>
                <Tooltip title="Fees have been manually added from Edit Venue screen">
                  <i className="da icon-venue-fees" />
                </Tooltip>
                <span>Venue Fees: </span>
                <span>${singleData.venue_fees}</span>
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Days to Event',
      dataIndex: 'days_to_event',
      canSort: true,
      width: '1.5%',
      thStyle: { width: '1.5%' },
      thClassName: 'text-center',
      tdStyle: { padding: 0, position: 'relative' },
      dataRender: (daysToEvent: number) => {
        const { toolTipText, className } = daysToEventColorCheck(
          daysToEvent,
          'list',
        );
        return (
          <Tooltip placement="left" title={toolTipText}>
            <div
              className={`text-center ${className}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                fontSize: '1.25rem',
                fontWeight: 400,
                cursor: 'default',
              }}
            >
              {daysToEvent}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Date & Time',
      dataIndex: 'event_date',
      canSort: true,
      width: '2.1%',
      thStyle: { width: '2.1%' },
      dataRender: (eventDate: string) => (
        <div>
          {eventDate
            ? formatDateGivenFormatString(eventDate, 'ddd, MMM DD, YYYY h:mm A')
            : '-'}
        </div>
      ),
    },
    {
      title: 'Broker',
      dataIndex: 'broker_name',
      width: '2.8%',
      thStyle: { width: '2.8%' },
      dataRender: (brokerName: string[] | null) =>
        showBrokerOrSeatMapCreatorCheckerLister(brokerName ?? []),
    },
    {
      title: 'Lister',
      dataIndex: 'lister_name',
      width: '2%',
      thStyle: { width: '2%' },
      dataRender: (listerName: string[] | null) =>
        showBrokerOrSeatMapCreatorCheckerLister(listerName ?? []),
    },
    {
      title: 'PO Lines',
      dataIndex: 'active_listings',
      canSort: true,
      width: '1.56%',
      thStyle: { width: '1.56%' },
      thClassName: 'text-right',
      dataRender: (activeListings: number) => (
        <div className="text-right">{formatNumericValues(activeListings)}</div>
      ),
    },
    {
      title: 'Pending Fills',
      dataIndex: 'pending_fills',
      canSort: true,
      width: '1.56%',
      thStyle: { width: '1.56%' },
      thClassName: 'text-right',
      dataRender: (pendingFills: number) => (
        <div className="text-right">{formatNumericValues(pendingFills)}</div>
      ),
    },
    {
      title: 'Pricing Issue',
      dataIndex: 'pricing_issue',
      canSort: true,
      width: '1.56%',
      thStyle: { width: '1.56%' },
      thClassName: 'text-right',
      dataRender: (pricingIssue: number) => (
        <div className="text-right">{formatNumericValues(pricingIssue)}</div>
      ),
    },
    {
      title: 'Market Price Updated On',
      dataIndex: 'market_price_updated_on',
      canSort: true,
      width: '2.1%',
      thStyle: { width: '2.1%' },
      dataRender: (marketPriceUpdatedOn: string | null) => (
        <div>
          {marketPriceUpdatedOn
            ? formatDateGivenFormatString(
                marketPriceUpdatedOn,
                'ddd, MMM DD, YYYY h:mm A',
              )
            : '-'}
        </div>
      ),
    },
    {
      title: 'Po Number',
      dataIndex: 'purchase_id',
      canSort: true,
      width: '2.3%',
      thStyle: { width: '2.3%' },
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      canSort: true,
      width: '1.8%',
      thStyle: { width: '1.8%' },
      thClassName: 'text-right',
      dataRender: (qty: number) => (
        <div className="text-right">{formatNumericValues(qty)}</div>
      ),
    },
    {
      title: 'Section',
      dataIndex: 'section',
      canSort: true,
      width: '2.68%',
      thStyle: { width: '2.68%' },
      tdStyle: { padding: '8px 8px' },
      dataRender: (section: string, otherData: any) => {
        const singleData: IPriceComparisonListing = otherData.data.singleData;
        if (!canEditListing) return <div>{section}</div>;
        return (
          <Input
            key={singleData.purchase_line_id}
            size="small"
            value={getEditedField(singleData.purchase_line_id, 'section', section)}
            onChange={(e) =>
              setEditedField(singleData.purchase_line_id, 'section', e.target.value)
            }
          />
        );
      },
    },
    {
      title: 'Row',
      dataIndex: 'row',
      canSort: true,
      width: '2.01%',
      thStyle: { width: '2.01%' },
      tdStyle: { padding: '8px 8px' },
      dataRender: (row: string, otherData: any) => {
        const singleData: IPriceComparisonListing = otherData.data.singleData;
        if (!canEditListing) return <div>{row}</div>;
        return (
          <Input
            key={singleData.purchase_line_id}
            size="small"
            value={getEditedField(singleData.purchase_line_id, 'row', row)}
            onChange={(e) =>
              setEditedField(singleData.purchase_line_id, 'row', e.target.value)
            }
          />
        );
      },
    },
    {
      title: 'Seats',
      dataIndex: 'seats',
      width: '1.56%',
      thStyle: { width: '1.56%' },
    },
    {
      title: 'Max Cost',
      dataIndex: 'cost_price',
      canSort: true,
      width: '1.56%',
      thStyle: { width: '1.56%' },
      thClassName: 'text-right',
      dataRender: (cost: number) => (
        <div className="text-right">{formatAccountingValues(cost)}</div>
      ),
    },
    {
      title: 'Alt. Cost',
      dataIndex: 'secondary_cost',
      width: '1.56%',
      thStyle: { width: '1.56%' },
      thClassName: 'text-right',
      dataRender: (cost: string | null) => (
        <div className="text-right">
          {cost === null || cost === undefined
            ? '-'
            : formatAccountingValues(Number(cost))}
        </div>
      ),
    },
    {
      title: 'List Price',
      dataIndex: 'list_price',
      canSort: true,
      width: '2.5%',
      thStyle: { width: '2.5%' },
      tdStyle: { padding: '8px 8px' },
      dataRender: (listPrice: number, otherData: any) => {
        const singleData: IPriceComparisonListing = otherData.data.singleData;
        const currentPrice = getEditedField(
          singleData.purchase_line_id,
          'list_price',
          listPrice,
        );
        const showListPriceWarning =
          currentPrice < singleData.cost_price || currentPrice === 0;
        // Fixed-width slot always reserved for the icon, visible or not, so
        // every row's input box is the same size whether or not the warning
        // fires -- otherwise a row without the warning stretches its input
        // to fill the space the icon would have taken.
        const listPriceWarning = (
          <span
            className="ml-5"
            style={{
              width: 18,
              display: 'inline-flex',
              justifyContent: 'center',
              visibility: showListPriceWarning ? 'visible' : 'hidden',
            }}
          >
            <Tooltip title={'List Price is 0 or less than Max Cost'}>
              <i className="da icon-warning" />
            </Tooltip>
          </span>
        );
        if (!canEditListing) {
          return (
            <div
              className="flx alignItemsCenter justifyContentFlexEnd"
              style={{ display: 'inline-flex' }}
            >
              <div className="text-right" style={{ width: 70 }}>
                {formatAccountingValues(listPrice)}
              </div>
              {listPriceWarning}
            </div>
          );
        }
        return (
          <div
            className="flx alignItemsCenter"
            style={{ display: 'inline-flex' }}
          >
            <InputNumber
              key={singleData.purchase_line_id}
              size="small"
              value={currentPrice}
              min={0}
              max={9999}
              precision={2}
              controls={false}
              style={{ width: 70, textAlign: 'right' }}
              onChange={(value) => {
                if (typeof value === 'number' && !isNaN(value)) {
                  setEditedField(
                    singleData.purchase_line_id,
                    'list_price',
                    value,
                  );
                }
              }}
            />
            {listPriceWarning}
          </div>
        );
      },
    },
    {
      title: 'Expected Market Price',
      dataIndex: 'list_price',
      width: '1.56%',
      thStyle: { width: '1.56%' },
      thClassName: 'text-right',
      dataRender: (_: any, otherData: any) => {
        const singleData: IPriceComparisonListing = otherData.data.singleData;
        if (!Number.isFinite(expectedMarketPriceFactor)) {
          return <div className="text-right">-</div>;
        }
        // Same as All Listing's own Expected Market Price column: recomputed
        // off the live (not-yet-saved) List Price draft, so it updates as
        // the user types, same as Zone Listings' editedActiveListings-driven
        // version does.
        const currentPrice = getEditedField(
          singleData.purchase_line_id,
          'list_price',
          singleData.list_price,
        );
        return (
          <div className="text-right">
            {formatAccountingValues(currentPrice * expectedMarketPriceFactor)}
          </div>
        );
      },
    },
    {
      title: 'Availability',
      dataIndex: 'availability_display_alias',
      canSort: true,
      width: '2.68%',
      thStyle: { width: '2.68%' },
      thClassName: 'text-center',
      dataRender: (_: string, otherData: any) => {
        const singleData: IPriceComparisonListing = otherData.data.singleData;
        const tagClassName =
          getTagClassForZones(singleData.availability, [])?.className ?? '';
        const showSeatFraction =
          singleData.is_web_scraping === 1 &&
          !!singleData.available_seats &&
          !!singleData.total_seats;
        const tagContent = showSeatFraction
          ? `${singleData.available_seats} / ${singleData.total_seats}`
          : (zoneAvailabilityShortLabels[singleData.availability] ?? '-');
        return (
          <div className="text-center">
            <Tag className={tagClassName} style={{ cursor: 'initial' }}>
              {tagContent}
            </Tag>
          </div>
        );
      },
    },
    /* Recommendation column commented out per request
    {
      title: "Recommendation",
      dataIndex: "recommendation",
      canSort: true,
      width: "3.24%",
      thStyle: { width: "3.24%" },
      thClassName: "text-center",
      dataRender: (recommendation: string | null) => {
        if (!recommendation) {
          return <div className="text-center">-</div>;
        }
        const tagClassName = getTagClassForRecommendation(recommendation) ?? "";
        return (
          <div className="text-center">
            <Tag className={tagClassName} style={{ cursor: "initial" }}>
              {recommendation}
            </Tag>
          </div>
        );
      },
    },
    */
    /* Recommended Adjusted Price column commented out per request
    {
      title: "Recommended Adjusted Price",
      dataIndex: "recommended_adjusted_price",
      canSort: true,
      width: "3.57%",
      thStyle: { width: "3.57%" },
      thClassName: "text-right",
      dataRender: (price: number | null) => (
        <div className="text-right">
          {price === null || price === undefined
            ? "-"
            : formatAccountingValues(price)}
        </div>
      ),
    },
    */
    {
      title: 'Zone Seating',
      dataIndex: 'is_zone_seating',
      width: '2.23%',
      thStyle: { width: '2.23%' },
      thClassName: 'text-center',
      dataRender: (isZoneSeating: boolean) =>
        isZoneSeating ? (
          <div className="text-center">
            <i className="da icon-zone-seating" />
          </div>
        ) : (
          <div className="text-center">
            <i className="da icon-cross-circle" />
          </div>
        ),
    },
    {
      title: 'Rank',
      dataIndex: 'row_competitive_rank',
      canSort: true,
      width: '2.01%',
      thStyle: { width: '2.01%' },
      thClassName: 'text-center',
      dataRender: (rank: string | null) => (
        <div className="text-center">{rank ?? '-'}</div>
      ),
    },
    {
      title: 'Adjusted Min Price',
      dataIndex: 'adjusted_lowest_price',
      canSort: true,
      width: '2.15%',
      thStyle: { width: '2.15%' },
      thClassName: 'text-right',
      dataRender: (price: number | null) => (
        <div className="text-right">
          {price === null || price === undefined
            ? '-'
            : formatAccountingValues(price)}
        </div>
      ),
    },
    {
      title: 'Adjusted Max Price',
      dataIndex: 'adjusted_max_price',
      canSort: true,
      width: '2.15%',
      thStyle: { width: '2.15%' },
      thClassName: 'text-right',
      dataRender: (price: number | null) => (
        <div className="text-right">
          {price === null || price === undefined
            ? '-'
            : formatAccountingValues(price)}
        </div>
      ),
    },
    {
      title: 'Adjusted Average Price',
      dataIndex: 'adjusted_average_price',
      canSort: true,
      width: '2.15%',
      thStyle: { width: '2.15%' },
      thClassName: 'text-right',
      dataRender: (price: number | null) => (
        <div className="text-right">
          {price === null || price === undefined
            ? '-'
            : formatAccountingValues(price)}
        </div>
      ),
    },
    {
      title: 'Adjusted Median Price',
      dataIndex: 'adjusted_median_price',
      canSort: true,
      width: '2.15%',
      thStyle: { width: '2.15%' },
      thClassName: 'text-right',
      dataRender: (price: number | null) => (
        <div className="text-right">
          {price === null || price === undefined
            ? '-'
            : formatAccountingValues(price)}
        </div>
      ),
    },
    {
      title: 'Row No. of Lowest Price',
      dataIndex: 'row_no_of_lowest_price',
      canSort: true,
      width: '2.15%',
      thStyle: { width: '2.15%' },
      thClassName: 'text-center',
      dataRender: (rowNo: string | null) => (
        <div className="text-center">{rowNo ?? '-'}</div>
      ),
    },
    {
      title: 'Internal Notes',
      dataIndex: 'internal_notes',
      width: '3%',
      thStyle: { width: '3%' },
    },
    {
      title: 'Listed On',
      dataIndex: 'listed_at',
      canSort: true,
      width: '3.5%',
      thStyle: { width: '3.5%' },
      dataRender: (listedAt: string) => (
        <div>
          {listedAt
            ? formatDateGivenFormatString(listedAt, 'MM/DD/YYYY h:mm A')
            : '-'}
        </div>
      ),
    },
    {
      title: 'Listed By',
      dataIndex: 'listed_by',
      canSort: true,
      width: '2.68%',
      thStyle: { width: '2.68%' },
    },
    {
      title: 'Inventory Tag',
      dataIndex: 'inventory_tag',
      width: '2.68%',
      thStyle: { width: '2.68%' },
    },
    {
      title: 'PO Tag',
      dataIndex: 'po_tags',
      width: '1.8%',
      thStyle: { width: '1.8%' },
      dataRender: (poTags: string[] | null) => (
        <div>{poTags && poTags.length ? poTags.join(', ') : '-'}</div>
      ),
    },
    {
      title: 'TB2.0',
      dataIndex: 'tb_placement_check',
      width: '1.56%',
      thStyle: { width: '1.56%' },
      thClassName: 'text-center',
      dataRender: (tbPlacement: boolean) => (
        <div className="text-center">{tbPlacement ? 'Yes' : 'No'}</div>
      ),
    },
    {
      title: '',
      dataIndex: 'purchase_line_id',
      width: '1.3%',
      thStyle: { width: '1.3%' },
      thClassName: 'text-center',
      dataRender: (_: number, otherData: any) => {
        const singleData: IPriceComparisonListing = otherData.data.singleData;
        if (!canEditListing) {
          return <></>;
        }
        if (savingRows[singleData.purchase_line_id]) {
          return (
            <div className="text-center">
              <LoadingSpinner isLoading={true} />
            </div>
          );
        }
        if (!isRowDirty(singleData)) {
          return <></>;
        }
        return (
          <div className="text-center">
            <Tooltip title="Save changes">
              <Typography.Link onClick={() => saveRow(singleData)}>
                <i className="da icon-save-floppy" />
              </Typography.Link>
            </Tooltip>
          </div>
        );
      },
    },
    /* Auto Adj. column commented out per request
    {
      title: 'Auto Adj.',
      dataIndex: 'auto_adjustment',
      width: '2.01%',
      thStyle: { width: '2.01%' },
      thClassName: 'text-center',
      dataRender: (autoAdjustment: boolean) => (
        <div className="text-center">
          <img
            src={
              autoAdjustment ? autoAdjustmentEnabled : autoAdjustmentDisabled
            }
            alt={
              autoAdjustment ? 'auto-adjust-enabled' : 'auto-adjust-disabled'
            }
          />
        </div>
      ),
    },
    */
    {
      title: '',
      dataIndex: 'broadcast',
      width: '1.56%',
      thStyle: { width: '1.56%' },
      thClassName: 'text-center',
      dataRender: (broadcast: boolean, otherData: any) => {
        const singleData: IPriceComparisonListing = otherData.data.singleData;
        return (
          <Tooltip
            title={broadcast ? 'Un-Broadcast' : 'Broadcast'}
            placement="left"
          >
            <Typography.Link
              onClick={() =>
                canToggleBroadcast ? toggleBroadcast(singleData) : null
              }
              className={canToggleBroadcast ? '' : 'notClickable'}
            >
              <i
                className={
                  broadcast ? 'da icon-network' : 'da icon-network-red'
                }
              />
            </Typography.Link>
          </Tooltip>
        );
      },
    },
  ];

  return dataFetchError ? (
    <CustomError {...dataFetchError} />
  ) : (
    <div className="removeTblLRSpc customTbl priceComparisonTbl">
      <CommonReportTable
        tableClass="customTbl scroll-y fixedHeaderFooter"
        tableHeight={{ maxHeight: 'calc(100vh - 196px)' }}
        headers={headers}
        data={listings}
        loading={showLoader}
        onSort={(sortIndex: string, sortOrder: 'asc' | 'desc') => {
          setQueryParams({
            ...queryParams,
            sort_column: sortIndex,
            sort_order: sortOrder,
          });
        }}
        sortField={{
          dataIndex: queryParams?.sort_column,
          order: queryParams?.sort_order,
        }}
        otherData={{}}
      />
    </div>
  );
};

export default PriceComparisonTable;
